import { useEffect, useState } from "react";
import { RenderFieldExtensionCtx } from "datocms-plugin-sdk";
import { Canvas, SwitchField, Spinner } from "datocms-react-ui";
import get from "lodash/get";
import { datoFocusRed, projectMap } from "../utils/consts";
import type {
  Experiment,
  Option,
  GroupedOption,
  AmpExpInputValue,
} from "../types";
import Select from "../components/CustomSelect";

interface AEVariantsSelectorProps {
  ctx: RenderFieldExtensionCtx;
}

const groupedOptionsInit: GroupedOption[] = [
  {
    label: "Dev",
    options: [],
  },
  {
    label: "Prod",
    options: [],
  },
];

export const AEVariantsSelector = ({ ctx }: AEVariantsSelectorProps) => {
  const [devMode, setDevMode] = useState(false);
  const currentValue: AmpExpInputValue = (() => {
    const formValue = get(ctx.formValues, ctx.fieldPath);
    if (!formValue) return null;
    return JSON.parse(formValue as string);
  })();
  const [items, setItems] = useState<Experiment[]>([]);
  const [groupedOptions, setGroupedOptions] =
    useState<GroupedOption[]>(groupedOptionsInit);
  const [error, setError] = useState(false);
  const [selectedValue, setSelectedValue] = useState<AmpExpInputValue | null>(currentValue);

  const updateVariants = (newValue: AmpExpInputValue | null) => {
    setSelectedValue(newValue);
    ctx.setFieldValue(ctx.fieldPath, JSON.stringify(newValue));
  };

  useEffect(() => {
    const formValue = get(ctx.formValues, ctx.fieldPath);
    const parsedValue = formValue ? JSON.parse(formValue as string) : null;
    setSelectedValue(parsedValue);
  }, [ctx.formValues, ctx.fieldPath]);

  useEffect(() => {
    // The proxy authenticates the caller with the editor's DatoCMS access token,
    // present only when the plugin has the `currentUserAccessToken` permission.
    const token = ctx.currentUserAccessToken;
    if (!token) {
      console.error(
        'Missing DatoCMS access token — grant this plugin the "Current user access token" permission.'
      );
      setError(true);
      return;
    }

    const fetchExperimentsAndFlags = async () => {
      try {
        // The plugin iframe and the proxy are served from the same deployment,
        // so a relative path resolves; the Amplitude credential stays server-side.
        const authHeaders = { Authorization: `Bearer ${token}` };
        const experimentsResponse = await fetch(
          "/api/amplitude-proxy?resource=experiments",
          { headers: authHeaders }
        );
        if (!experimentsResponse.ok) {
          throw new Error(`Proxy returned ${experimentsResponse.status}`);
        }
        const experimentsResult: { experiments: Experiment[] } =
          await experimentsResponse.json();

        const flagsResponse = await fetch("/api/amplitude-proxy?resource=flags", {
          headers: authHeaders,
        });
        if (!flagsResponse.ok) {
          throw new Error(`Proxy returned ${flagsResponse.status}`);
        }
        const flagsResult: { flags: Experiment[] } =
          await flagsResponse.json();

        setItems([...experimentsResult.experiments, ...flagsResult.flags]);
      } catch (error) {
        console.error("Failed to fetch experiments and flags: ", error);
        setError(true);
      }
    };
    fetchExperimentsAndFlags();
  }, [ctx.currentUserAccessToken]);

  useEffect(() => {
    const devOptions: Option[] = [];
    const prodOptions: Option[] = [];

    items?.forEach((item) => {
      item.variants.forEach((variant) => {
        const option: Option = {
          label: `${item.key}: ${variant.key}`,
          value: {
            expId: item.id,
            variants: { [item.key]: variant.key },
          },
          env: projectMap[item.projectId],
        };

        if (projectMap[item.projectId] === "dev") {
          devOptions.push(option);
        } else {
          prodOptions.push(option);
        }
      });
    });

    const newGroupedOptions: GroupedOption[] = [
      {
        label: "Dev",
        options: devOptions,
      },
      {
        label: "Prod",
        options: prodOptions,
      },
    ];

    setGroupedOptions(newGroupedOptions);
  }, [items]);

  const filteredGroupedOptions = devMode
    ? groupedOptions
    : groupedOptions.filter((group) => !group.label.includes("Dev"));
  const hasOptions = groupedOptions.some((group) => group.options.length > 0);

  const getSelectedOption = (): Option | null => {
    if (!selectedValue) return null;
    
    for (const group of groupedOptions) {
      for (const option of group.options) {
        const optionValue = option.value;
        
        const expIdMatches = optionValue.expId === selectedValue.expId;
        const variantsMatch = JSON.stringify(optionValue.variants) === JSON.stringify(selectedValue.variants);
        
        if (expIdMatches && variantsMatch) {
          return option;
        }
      }
    }
    return null;
  };

  return (
    <Canvas ctx={ctx}>
      {error ? (
        <p style={{ color: datoFocusRed }}>
          {"* Failed to fetch data. Please refresh the page."}
        </p>
      ) : hasOptions ? (
        <>
          <Select
            groupedOptions={filteredGroupedOptions}
            setValue={(event) => updateVariants(event?.value)}
            value={getSelectedOption()}
          />
          <div style={{ marginTop: "12px" }}>
            <SwitchField
              name="devMode"
              id="devMode"
              label="Show Dev Variants"
              value={devMode}
              onChange={(newValue) => setDevMode(newValue)}
            />
          </div>
        </>
      ) : (
        <div>
          {"Loading Variants..."} <Spinner size={24} />
        </div>
      )}
    </Canvas>
  );
};
